import type {
  CreateExpense,
  CreateExpenseDraft,
  DocumentResponse,
  Expense,
  ExpenseAggregateFilters,
  ExpenseAggregateResponse,
  ExpenseCategory,
  ExpenseFilters,
  RecordExpenseReimbursement,
  UpdateExpense,
} from '@scrambl/shared';

import { buildQueryString } from './base-api';
import { apiClient } from './api-client';
import { createSingletonApi } from './singleton-factory';

export interface ExpenseListResponse {
  data: Expense[];
  total: number;
}

export interface ExpenseModuleCounts {
  mine: number;
  inbox: number;
  all: number;
  reimbursements: number;
}

export interface ExpenseDetailCounts {
  documents: number;
  notes: number;
}

export interface ExpenseListParams extends Partial<ExpenseFilters> {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface FinancialsExpenseRow {
  id: string;
  expenseDate: string;
  memberId: string;
  memberName: string;
  categoryName: string | null;
  merchant: string;
  description: string | null;
  billable: boolean;
  status: string;
  authorization: { id: string; label: string } | null;
  amount: number;
  invoicedAt: string | null;
}

export interface FinancialsExpensesResponse {
  data: FinancialsExpenseRow[];
  total: number;
  totals: {
    totalAmount: number;
    billableAmount: number;
    nonBillableAmount: number;
  };
}

export interface FinancialsExpensesParams {
  entityId?: string;
  projectId?: string;
  startDate?: string;
  endDate?: string;
  memberIds?: string[];
  categoryIds?: string[];
  authorizationId?: string;
  billable?: boolean;
  status?: string[];
  page?: number;
  limit?: number;
}

class ExpensesApiService {
  private readonly baseEndpoint = '/expenses';

  async listExpenses(params?: ExpenseListParams): Promise<ExpenseListResponse> {
    const queryString = buildQueryString(params as Record<string, unknown>);
    return apiClient<ExpenseListResponse>(`${this.baseEndpoint}${queryString}`);
  }

  async getFinancialsExpenses(
    params: FinancialsExpensesParams,
  ): Promise<FinancialsExpensesResponse> {
    const queryString = buildQueryString(params as Record<string, unknown>);
    return apiClient<FinancialsExpensesResponse>(
      `${this.baseEndpoint}/financials${queryString}`,
    );
  }

  async aggregate(body: ExpenseAggregateFilters): Promise<ExpenseAggregateResponse> {
    return apiClient<ExpenseAggregateResponse>(`${this.baseEndpoint}/aggregate`, {
      method: 'POST',
      body,
    });
  }

  async getExpense(id: string): Promise<Expense> {
    return apiClient<Expense>(`${this.baseEndpoint}/${id}`);
  }

  async createExpense(data: CreateExpense): Promise<Expense> {
    return apiClient<Expense>(this.baseEndpoint, {
      method: 'POST',
      body: data,
    });
  }

  async createExpenseDraft(data: CreateExpenseDraft = {}): Promise<Expense> {
    return apiClient<Expense>(`${this.baseEndpoint}/drafts`, {
      method: 'POST',
      body: data,
    });
  }

  async updateExpense(id: string, data: UpdateExpense): Promise<Expense> {
    return apiClient<Expense>(`${this.baseEndpoint}/${id}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteExpense(id: string): Promise<void> {
    await apiClient(`${this.baseEndpoint}/${id}`, {
      method: 'DELETE',
    });
  }

  async submitExpense(id: string): Promise<Expense> {
    return apiClient<Expense>(`${this.baseEndpoint}/${id}/submit`, {
      method: 'POST',
    });
  }

  async recallExpense(id: string): Promise<Expense> {
    return apiClient<Expense>(`${this.baseEndpoint}/${id}/recall`, {
      method: 'POST',
    });
  }

  async approveExpense(id: string): Promise<Expense> {
    return apiClient<Expense>(`${this.baseEndpoint}/${id}/approve`, {
      method: 'POST',
    });
  }

  async requestExpenseChanges(id: string, comment?: string): Promise<Expense> {
    return apiClient<Expense>(`${this.baseEndpoint}/${id}/request-changes`, {
      method: 'POST',
      body: { comment },
    });
  }

  async rejectExpense(id: string, comment?: string): Promise<Expense> {
    return apiClient<Expense>(`${this.baseEndpoint}/${id}/reject`, {
      method: 'POST',
      body: { comment },
    });
  }

  async recordReimbursement(id: string, data: RecordExpenseReimbursement): Promise<Expense> {
    return apiClient<Expense>(`${this.baseEndpoint}/${id}/reimburse`, {
      method: 'POST',
      body: data,
    });
  }

  async getCategories(): Promise<ExpenseCategory[]> {
    return apiClient<ExpenseCategory[]>(`${this.baseEndpoint}/categories`);
  }

  async createCategory(data: Partial<ExpenseCategory> & { name: string }): Promise<ExpenseCategory> {
    return apiClient<ExpenseCategory>(`${this.baseEndpoint}/categories`, {
      method: 'POST',
      body: data,
    });
  }

  async updateCategory(id: string, data: Partial<ExpenseCategory>): Promise<ExpenseCategory> {
    return apiClient<ExpenseCategory>(`${this.baseEndpoint}/categories/${id}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await apiClient(`${this.baseEndpoint}/categories/${id}`, {
      method: 'DELETE',
    });
  }

  async getModuleCounts(): Promise<ExpenseModuleCounts> {
    return apiClient<ExpenseModuleCounts>(`${this.baseEndpoint}/module-counts`);
  }

  async getDetailCounts(id: string): Promise<ExpenseDetailCounts> {
    return apiClient<ExpenseDetailCounts>(`${this.baseEndpoint}/${id}/counts`);
  }

  async uploadReceipt(
    id: string,
    file: File,
    categoryId?: string,
  ): Promise<DocumentResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (categoryId) {
      formData.append('categoryId', categoryId);
    }

    return apiClient<DocumentResponse>(`${this.baseEndpoint}/${id}/receipts/upload`, {
      method: 'POST',
      body: formData,
      headers: {},
    });
  }
}

export const expensesApi = createSingletonApi(ExpensesApiService);
